import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import swal from "sweetalert";
import useThemeColor from "../../hooks/useThemeColor";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import AddButton from "../../components/CustomComponents/Buttons/AddButton/AddButton";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import {
  setVisibleColumns,
  setSortOrder,
  setSortBy,
  setFilterField,
} from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import ModernGridToolbar from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ModernGridPagerFooter from "../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter";
import GridEmptyState from "../../components/CustomComponents/ModernGridToolbar/GridEmptyState";
import TableActionsMenu, {
  TableActionsItem,
} from "../../components/CustomComponents/TableActionsMenu/TableActionsMenu";
import SortableColumnHeader from "../../components/CustomComponents/ModernGridToolbar/SortableColumnHeader";
import useResetPageOnFilterChange from "../../hooks/useResetPageOnFilterChange";
import useDebouncedEffect from "../../hooks/useDebouncedEffect";
import useInvalidateFetchOnFilterChange from "../../hooks/useInvalidateFetchOnFilterChange";
import { applyCatalogTotalFromFetch } from "../../utils/catalogTotalRecords";


import StatusBadge from "../../components/CustomComponents/StatusBadge/StatusBadge";
import CustomModal from "../../components/CustomComponents/modal/customModal";

const MENU_SORT_FIELDS = {
  name: "Menu_Name",
  sortOrder: "Menu_Orderby",
  id: "Menu_ID",
};

const MENU_SORT_LABELS = {
  [MENU_SORT_FIELDS.name]: "Name",
  [MENU_SORT_FIELDS.sortOrder]: "Sort Order",
  [MENU_SORT_FIELDS.id]: "ID",
};

const ManageMenus = () => {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [menu, setMenu] = useState([]);
  const pageKey = "menus";
  const defaultVisibleColumns = {
    status: true,
    businessHours: true,
    kiosk: true,
    onlineOrders: true,
    delivery: true,
    epos: true,
  };
  const defaultSortOrder = { value: "ASC", label: "ASC" };
  const defaultSortBy = { value: "Menu_Orderby", label: "Order By" };
  const defaultPageSize = { value: "50", label: "50" };
  const validPageSizes = ["20", "50", "100"];
  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const pageSize =
    preferences.pageSize &&
    validPageSizes.includes(String(preferences.pageSize.value))
      ? preferences.pageSize
      : defaultPageSize;
  const dispatch = useDispatch();

  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [catalogTotalRecords, setCatalogTotalRecords] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const menuFetchSeqRef = useRef(0);

  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const lastSelectedIndex = useRef(null);
  const [statusConfirmMenu, setStatusConfirmMenu] = useState(null);

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const SHOW_BULK_ACTIONS = false;

  const handlePageSizeChange = useCallback(
    (value) => {
      dispatch(setFilterField({ pageKey, field: "pageSize", value }));
    },
    [dispatch, pageKey]
  );

  const hasFiltersApplied = () => search.trim() !== "";

  const clearFilters = () => {
    setSearch("");
  };

  const handleColumnSort = useCallback(
    (sortField) => {
      const nextOrder =
        sortBy?.value === sortField && sortOrder?.value === "DESC"
          ? { value: "ASC", label: "ASC" }
          : { value: "DESC", label: "DESC" };

      dispatch(
        setSortBy({
          pageKey,
          sortBy: {
            value: sortField,
            label: MENU_SORT_LABELS[sortField] ?? sortField,
          },
        })
      );
      dispatch(setSortOrder({ pageKey, sortOrder: nextOrder }));
    },
    [dispatch, pageKey, sortBy?.value, sortOrder?.value]
  );

  const navigate = useNavigate();
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  useEffect(() => {
    // Check if preferences exist and merge with defaults to ensure new columns are included
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else {
      // Check if preferences need updating (missing new columns or have removed columns)
      if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
        // Merge existing preferences with default columns (this handles both additions and removals)
        const mergedColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
        dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
      }
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    if (
      !preferences.pageSize ||
      !validPageSizes.includes(String(preferences.pageSize?.value))
    ) {
      dispatch(setFilterField({ pageKey, field: "pageSize", value: defaultPageSize }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageKey]);

  const fetchMenuData = useCallback(async () => {
    const fetchSeq = ++menuFetchSeqRef.current;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_MENU_LIST, {
        params: {
          CompID,
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
          Search: search?.trim() || "",
        },
      });

      if (fetchSeq !== menuFetchSeqRef.current) return;

      const MenuData = response.data.data || response.data;
      if (Array.isArray(MenuData)) {
        setMenu(MenuData);
        if (!search.trim()) {
          applyCatalogTotalFromFetch(setCatalogTotalRecords, MenuData.length);
        }
      } else {
        setMenu([]);
      }
    } catch (err) {
      if (fetchSeq !== menuFetchSeqRef.current) return;
      setMenu([]);
      showErrorToast("Failed to fetch menus.");
    } finally {
      if (fetchSeq === menuFetchSeqRef.current) {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    }
  }, [search, sortBy, sortOrder, CompID]);

  const totalRecords = menu.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / Number(pageSize.value)));
  const pagedMenu = useMemo(() => {
    const size = Number(pageSize.value);
    const start = (pageNumber - 1) * size;
    return menu.slice(start, start + size);
  }, [menu, pageNumber, pageSize]);

  const getMenuGlobalIndex = (menuItem) =>
    menu.findIndex((m) => m.menu_ID === menuItem.menu_ID);

  useResetPageOnFilterChange(setPageNumber, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useInvalidateFetchOnFilterChange(menuFetchSeqRef, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useDebouncedEffect(() => fetchMenuData(), [fetchMenuData]);

  useEffect(() => {
    if (!initialLoadComplete) return;
    setLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sortBy?.value, sortOrder?.value]);

  useEffect(() => {
    if (pageNumber > totalPages) {
      setPageNumber(totalPages);
    }
  }, [pageNumber, totalPages]);

  const handleAddMenu = () => navigate("/menu/addMenu");

  const handleDeleteMenu = async (menuItem) => {
    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${menuItem.menu_Name}?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      const response = await axiosInstance.delete(APIs.DELETE_MENU, {
        params: {
          CompID,
          Menu_ID: menuItem.menu_ID,
        },
      });

      if (response.data.status) {
        setMenu((prevMenu) =>
          prevMenu.filter((item) => item.menu_ID !== menuItem.menu_ID)
        );
        showSuccessToast("Menu is Deleted.");
      } else {
        showErrorToast(response.data.message || "Deletion failed");
      }
    } catch (err) {
      // Error in handleDeleteMenu silently
      showErrorToast("Something Went Wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectItem = (menuId, index, event) => {
    setSelectedItems((prev) => {
      const newSelected = new Set(prev);

      if (event.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);
        for (let i = start; i <= end; i++) {
          if (menu[i]?.menu_ID) {
            newSelected.add(menu[i].menu_ID);
          }
        }
      } else {
        if (newSelected.has(menuId)) {
          newSelected.delete(menuId);
        } else {
          newSelected.add(menuId);
        }
        lastSelectedIndex.current = index;
      }

      return newSelected;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(menu.map((item) => item.menu_ID));
      setSelectedItems(newSelected);
      lastSelectedIndex.current = null;
    } else {
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    }
  };

  const handleDeselectAll = () => {
    setSelectedItems(new Set());
    lastSelectedIndex.current = null;
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) {
      showErrorToast("No menus selected for deletion.");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${selectedItems.size} selected menus?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedItems).map((menuId) =>
          axiosInstance.delete(APIs.DELETE_MENU, {
            params: {
              CompID,
              Menu_ID: menuId,
            },
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      fetchMenuData();
      showSuccessToast(`${selectedItems.size} menus deleted successfully.`);
    } catch (err) {
      // Error in bulk delete silently
      showErrorToast("Failed to delete selected menus.");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkEdit = () => {
    if (selectedItems.size === 0) {
      showErrorToast("No menus selected for editing.");
      return;
    }

    const selectedMenuIds = Array.from(selectedItems);
    navigate(`/menu/bulkEdit`, { state: { selectedMenuIds } });
  };

  const handleBulkStatusChange = async (publishStatus) => {
    if (selectedItems.size === 0) {
      showErrorToast("No menus selected for status change.");
      return;
    }

    try {
      const confirmStatusChange = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to ${publishStatus ? "publish" : "unpublish"
          } ${selectedItems.size} selected menus?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmStatusChange) return;

      setLoading(true);
      const updatedMenu = menu.map((item) =>
        selectedItems.has(item.menu_ID)
          ? { ...item, menu_Publish: publishStatus }
          : item
      );
      setMenu(updatedMenu);

      await Promise.all(
        Array.from(selectedItems).map((menuId) =>
          axiosInstance.post(APIs.POST_UPDATE_MENU_STATUS, {
            CompID,
            Menu_ID: menuId,
            Menu_Publish: publishStatus,
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      showSuccessToast(
        `${selectedItems.size} menus status updated successfully.`
      );
    } catch (error) {
      // Error in bulk status change silently
      showErrorToast("Failed to update selected menus status.");
      fetchMenuData();
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, index) => {
    dragItem.current = index;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/json", JSON.stringify(index));
    e.currentTarget.style.opacity = "0.5";
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    dragOverItem.current = index;
    e.currentTarget.style.backgroundColor = "#e9ecef";
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e) => {
    e.preventDefault();

    const draggedIndex = JSON.parse(e.dataTransfer.getData("application/json"));
    const overIndex = dragOverItem.current;

    if (draggedIndex === overIndex) {
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }

    let newMenu = [...menu];
    let oldMenu = [...menu];
    const [draggedMenu] = newMenu.splice(draggedIndex, 1);
    newMenu.splice(overIndex, 0, draggedMenu);

    newMenu = newMenu.map((item, idx) => ({
      ...item,
      menu_Orderby: idx + 1,
    }));

    setMenu(newMenu);

    const FilteredData = newMenu.map((item, index) => ({
      Menu_ID: item.menu_ID,
      Menu_Orderby: index + 1,
    }));

    try {
      await axiosInstance.post(APIs.POST_UPDATE_MENU_SORT_ORDER, {
        CompID: CompID,
        Updates: FilteredData,
      });
      showSuccessToast("Menu order updated successfully.");
    } catch (error) {
      // Error updating menu order silently
      showErrorToast("Failed to update menu order.");
      setMenu(oldMenu);
    }

    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = "1";
    document.querySelectorAll("tr").forEach((tr) => {
      tr.style.backgroundColor = "";
    });
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const confirmMenuStatusChange = async () => {
    if (!statusConfirmMenu) return;

    const { menu_ID, menu_Publish, menu_Name } = statusConfirmMenu;
    const newStatus = !menu_Publish;

    setMenu((prev) =>
      prev.map((item) =>
        item.menu_ID === menu_ID ? { ...item, menu_Publish: newStatus } : item
      )
    );
    setStatusConfirmMenu(null);

    try {
      await axiosInstance.post(APIs.POST_UPDATE_MENU_STATUS, {
        CompID: CompID,
        Menu_ID: menu_ID,
        Menu_Publish: newStatus,
      });
      showSuccessToast(
        `"${menu_Name}" is now ${newStatus ? "Active" : "InActive"}.`
      );
    } catch (error) {
      setMenu((prev) =>
        prev.map((item) =>
          item.menu_ID === menu_ID ? { ...item, menu_Publish } : item
        )
      );
      showErrorToast("Failed to update menu status.");
    }
  };

  const showEmptyCatalogState =
    initialLoadComplete &&
    !loading &&
    !hasFiltersApplied() &&
    catalogTotalRecords === 0;

  const showGridToolbar = initialLoadComplete && !showEmptyCatalogState;

  const getEmptyStateDescription = () => {
    if (search.trim()) {
      return `No menus match your search "${search.trim()}". Try different keywords or clear filters.`;
    }
    return "There are no menus to show right now. Add a new menu or check back later.";
  };

  const renderNoDataMessage = () => {
    if (loading || menu.length > 0) return null;

    return (
      <GridEmptyState
        isDarkMode={isDarkMode}
        themeColor={themeColor}
        title="No menus found"
        description={getEmptyStateDescription()}
        onClearFilters={hasFiltersApplied() ? clearFilters : undefined}
        clearFiltersLabel="Clear all filters"
      />
    );
  };

  // Compute selected menus for button visibility
  const selectedMenus = menu.filter(item => selectedItems.has(item.menu_ID));
  const anyUnpublished = selectedMenus.some(item => !item.menu_Publish);
  const anyPublished = selectedMenus.some(item => item.menu_Publish);

  return (
    <div
      style={{
        position: "relative",
        minHeight: "100vh",
        paddingBottom: selectedItems.size > 0 ? "100px" : "0",
      }}
    >
<div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Manage Menu</li>
          </ol>
          <h1 className="page-header mb-0">Manage Menu</h1>
        </div>
        <div className="ms-auto">
          <button
            className="btn px-4"
            onClick={handleAddMenu}
            style={{ 
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: 'none',
              color: 'white',
              borderRadius: '8px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              boxShadow: `0 2px 8px ${themeColor}40`
            }}
            onMouseEnter={(e) => {
              e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
            }}
            onMouseLeave={(e) => {
              e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
            }}
          >
            <i className="fa fa-plus fa-lg me-2 ms-n2 text-white"></i>
            Add Menu
          </button>
        </div>
      </div>

      <div className="mb-3 mt-2">
        {!initialLoadComplete && <GridSectionLoader loading minHeight="400px" />}

        {showEmptyCatalogState && (
          <AddButton onClick={handleAddMenu} title="Add Menu" />
        )}

        {showGridToolbar && (
          <ModernGridToolbar
            isDarkMode={isDarkMode}
            title="Menus"
            totalCount={totalRecords}
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by menu name..."
            bodyFlush
            headerControls={
              <ColumnSelection
                visibleColumns={visibleColumns}
                setVisibleColumns={(cols) =>
                  dispatch(setVisibleColumns({ pageKey, columns: cols }))
                }
              />
            }
          >
            <div className="px-3 pb-3">
              <GridSectionLoader loading={loading} minHeight="280px">
                {renderNoDataMessage()}

                {!loading && pagedMenu.length > 0 && (
                  <div
                    className="table-responsive table-container"
                    style={{
                      minHeight: "200px",
                      maxHeight: "450px",
                      overflowY: "auto",
                      overflowX: "auto",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    <table
                      className="table table-hover"
                      style={{ "--modern-grid-sort-active": themeColor }}
                    >
                      <thead
                        className="bg-light sticky-header"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 10000,
                          backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                          color: isDarkMode ? "#ffffff" : "#000000",
                        }}
                      >
                        <tr>
                          <th className="pt-2 pb-2" />
                          <SortableColumnHeader
                            sortField={MENU_SORT_FIELDS.name}
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSort={handleColumnSort}
                          >
                            Name
                          </SortableColumnHeader>
                          {visibleColumns.businessHours && (
                            <th className="pt-2 pb-2 text-start">Business Hours</th>
                          )}
                          {visibleColumns.kiosk && (
                            <th className="pt-2 pb-2 text-center">KIOSK</th>
                          )}
                          {visibleColumns.onlineOrders && (
                            <th className="pt-2 pb-2 text-center">Online Orders</th>
                          )}
                          {visibleColumns.delivery && (
                            <th className="pt-2 pb-2 text-center">Delivery</th>
                          )}
                          {visibleColumns.epos && (
                            <th className="pt-2 pb-2 text-center">E-POS</th>
                          )}
                          {visibleColumns.status && (
                            <th className="pt-2 pb-2 text-center">Status</th>
                          )}
                          <th className="pt-2 pb-2 text-start">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedMenu.map((menuItem) => {
                          const globalIndex = getMenuGlobalIndex(menuItem);
                          return (
                            <tr
                              key={menuItem.menu_ID}
                              draggable
                              onDragStart={(e) => handleDragStart(e, globalIndex)}
                              onDragEnter={(e) => handleDragEnter(e, globalIndex)}
                              onDragLeave={handleDragLeave}
                              onDragOver={handleDragOver}
                              onDrop={handleDrop}
                              onDragEnd={handleDragEnd}
                            >
                              <td className="align-middle">
                                <i className="fa fa-bars text-secondary" style={{ cursor: "grab" }} />
                              </td>
                              <td
                                className="align-middle text-start"
                                onClick={() => navigate(`/menu/updateMenu/${menuItem.menu_ID}`)}
                                style={{ cursor: "pointer" }}
                              >
                                {menuItem.menu_Name}
                              </td>
                              {visibleColumns.businessHours && (
                                <td
                                  className="align-middle text-start"
                                  onClick={() => navigate(`/menu/updateMenu/${menuItem.menu_ID}`)}
                                  style={{ cursor: "pointer" }}
                                >
                                  {(menuItem.isAvailableInTimeIntervals ?? menuItem.IsAvailableInTimeIntervals)
                                    ? "Yes"
                                    : "No"}
                                </td>
                              )}
                              {visibleColumns.kiosk && (
                                <td
                                  className="align-middle text-center"
                                  onClick={() => navigate(`/menu/updateMenu/${menuItem.menu_ID}`)}
                                  style={{ cursor: "pointer" }}
                                >
                                  {(menuItem.isActiveforKIOSK ?? menuItem.IsActiveforKIOSK) ? "Yes" : "No"}
                                </td>
                              )}
                              {visibleColumns.onlineOrders && (
                                <td
                                  className="align-middle text-center"
                                  onClick={() => navigate(`/menu/updateMenu/${menuItem.menu_ID}`)}
                                  style={{ cursor: "pointer" }}
                                >
                                  {(menuItem.isActiveforTakeAway ?? menuItem.IsActiveforTakeAway) ? "Yes" : "No"}
                                </td>
                              )}
                              {visibleColumns.delivery && (
                                <td
                                  className="align-middle text-center"
                                  onClick={() => navigate(`/menu/updateMenu/${menuItem.menu_ID}`)}
                                  style={{ cursor: "pointer" }}
                                >
                                  {(menuItem.isActiveforDelivery ?? menuItem.IsActiveforDelivery) ? "Yes" : "No"}
                                </td>
                              )}
                              {visibleColumns.epos && (
                                <td
                                  className="align-middle text-center"
                                  onClick={() => navigate(`/menu/updateMenu/${menuItem.menu_ID}`)}
                                  style={{ cursor: "pointer" }}
                                >
                                  {(menuItem.isActiveforTill ?? menuItem.IsActiveforTill) ? "Yes" : "No"}
                                </td>
                              )}
                              {visibleColumns.status && (
                                <td className="align-middle text-center">
                                  <StatusBadge
                                    isActive={menuItem.menu_Publish}
                                    onClick={() => setStatusConfirmMenu(menuItem)}
                                  />
                                </td>
                              )}
                              <td className="align-middle text-start">
                                <TableActionsMenu dropup menuStyle={{ zIndex: 9999 }}>
                                  <TableActionsItem
                                    as={Link}
                                    to={`/menu/updateMenu/${menuItem.menu_ID}`}
                                    icon="fa fa-edit"
                                  >
                                    Edit
                                  </TableActionsItem>
                                  <TableActionsItem
                                    danger
                                    icon="fa fa-trash"
                                    onClick={() => handleDeleteMenu(menuItem)}
                                  >
                                    Delete
                                  </TableActionsItem>
                                </TableActionsMenu>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                <ModernGridPagerFooter
                  pageSize={pageSize}
                  onPageSizeChange={handlePageSizeChange}
                  totalRecords={totalRecords}
                  pageNumber={pageNumber}
                  totalPages={totalPages}
                  setPageNumber={setPageNumber}
                />
              </GridSectionLoader>
            </div>
          </ModernGridToolbar>
        )}
      </div>

      <CustomModal
        title="Change menu status?"
        subtitle={
          statusConfirmMenu
            ? `Are you sure you want to set "${statusConfirmMenu.menu_Name}" to ${
                statusConfirmMenu.menu_Publish ? "InActive" : "Active"
              }?`
            : ""
        }
        fontSize="18px"
        fontWeight={400}
        onClose={() => setStatusConfirmMenu(null)}
        isOpen={!!statusConfirmMenu}
        isCentered={true}
        showButton={true}
        showSubmitButton={true}
        showCancelButton={true}
        submitButtonText="Confirm"
        onSubmit={confirmMenuStatusChange}
        dialogMaxWidth="400px"
        minHeight="auto"
        maxHeight="320px"
      />
    </div>
  );
};

export default ManageMenus;